import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { format as formatTimeStampToDate } from 'date-fns';
import defaultTo from 'lodash/defaultTo';
import differenceBy from 'lodash/differenceBy';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import startCase from 'lodash/startCase';
import React, { useMemo } from 'react';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Field } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/editor/add';
import SectionMessage from '@atlaskit/section-message';
import Textfield from '@atlaskit/textfield';
import { PricingPlanCycle, PricingPlanType, Currency } from '@atlassian/cssxp-ccp-types';
import { getBillEstimate, getBillEstimateV2 } from '@atlassian/temp-proxies';
import { OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { InvoiceGroupFormField, InvoiceGroupSelectItem, useFeatureToggle } from '@css-xp/base';
import { formatDateToMonthDay, addDaysToDate, isLoomProduct } from '@css-xp/ppc-utils';
import {
  EntitlementDetails,
  Offering,
  PromoCodeDetails,
  Promotion,
  BillingFrequency,
  FetchAvailablePricingPlansPpcQuery,
  FetchAvailablePricingPlansPpcQueryVariables,
  FetchTransactionAccountForChangeSubscriptionPaneQuery,
  InvoiceGroup,
} from '@css-xp/graphtypes/src/generated-types-new';
import { OrderIntentRequest } from '@atlassian/cssxp-typescommon';
import { PARTNER_DISCOUNT_TYPE, DATE_FORMAT, STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { PricingType } from '@atlassian/cssxp-types-promotion';
import { DELETE_SANDBOX_URL } from '../../constants/urls';
import { DefaultEntitlementData, FormData } from './change-subscription-pane';
import { CurrentPlanSummary } from './current-plan-summary';
import { POUpdate } from './manage-po-update';
import { regexForPromoCodeKey } from './manage-promotion';
import { NewPlanSummary } from './new-plan-summary';
import { PriceDifference } from './price-difference';
import { getPricingEligibility, getDefaultPricingPlan } from './utils/change-subscription-pane-utils';
import { fetchAvailablePricingPlans } from './queries/fetch-available-pricing-plans';
import './change-subscription-pane.scss';

interface Props {
  entitlement: EntitlementDetails;
  formState: FormData;
  availableInvoiceGroups?: NonNullable<
    FetchTransactionAccountForChangeSubscriptionPaneQuery['transactionAccount']['availableInvoiceGroups']
  >['nodes'];
  isSkipTrialChecked: boolean;
  availableOfferings: Offering[];
  onDiscretionaryEstimateChange: (_obj: DiscretionaryEstimate) => void;
  isBillingCycleChanged?: boolean;
  // isUpgradeTrialScenario: boolean;
  isExistingAnnualPlanForJCA?: boolean;
  setGrandTotal?: (_value: number) => void;
  isOfferingChanged?: boolean;
  setCurrentProgressIndex: (_value: number) => void;
  isCurrentEntitlementFree: boolean;
  validPromoCodeData: Partial<PromoCodeDetails[]>;
  isPaidToFreeTransition?: boolean;
  isPremiumToStandardTransition?: boolean;
  availablePartnerPromotion: Promotion | undefined;
  updateFormState: (_data: FormData) => void;
  billToPartyPricingEligibilty?: string | null;
  txaId: string;
  isSearchable?: boolean;
  isEligibleForPremiumTrial?: boolean;
  defaultEntitlementData?: Partial<DefaultEntitlementData>;
  setPricingPlanId?: (value: string | null) => void;
  isAppEditionDowngrade?: boolean;
  isEligibleForAdvancedTrial?: boolean;
}

type Estimate = {
  response?: any;
  error?: any;
  loading?: boolean;
};

export interface DiscretionaryEstimate {
  error?: any;
  discretionaryDiscountAmount?: number;
  loading: boolean;
  isDisabled?: boolean;
}

export enum BehaviourAtEndOfTrial {
  CONVERT_TO_PAID = 'CONVERT_TO_PAID',
  CANCEL = 'CANCEL',
  REVERT_TRIAL = 'REVERT_TRIAL',
}

type PromotionInstanceDetail = Promotion & { promotionInstanceId?: string };

const activePromotionsValueResolver = (promotion: PromotionInstanceDetail) => {
  return {
    promotionDefinition: {
      promotionId: promotion.id,
      customisedValues: {
        applicationReason: {
          id: promotion.applicationReason?.id,
        },
        benefits: promotion.benefits,
      },
    },
  };
};

const ListContainer = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${token('space.200')};
  padding-left: ${token('space.200')};
`;

const addonCancellationWarningMessage = 'Any linked add-on subscriptions will be cancelled.';

export const ReviewChange = ({
  entitlement,
  formState,
  availableInvoiceGroups,
  isSkipTrialChecked,
  onDiscretionaryEstimateChange,
  isBillingCycleChanged,
  // isUpgradeTrialScenario,
  availableOfferings,
  isExistingAnnualPlanForJCA,
  setGrandTotal,
  isOfferingChanged,
  setCurrentProgressIndex,
  isCurrentEntitlementFree,
  validPromoCodeData,
  isPaidToFreeTransition,
  isPremiumToStandardTransition,
  availablePartnerPromotion,
  updateFormState,
  billToPartyPricingEligibilty,
  txaId,
  isSearchable,
  isEligibleForPremiumTrial,
  defaultEntitlementData,
  setPricingPlanId,
  isAppEditionDowngrade,
  isEligibleForAdvancedTrial,
}: Props) => {
  const [canUpdatePO, setCanUpdatePO] = React.useState<boolean>(false);
  const [estimatedBill, setEstimatedBill] = React.useState<Partial<Estimate>>({ loading: true });
  const [newBillingStartDate, setNewBillingStartDate] = React.useState('');

  const [isEnabled] = useFeatureToggle();

  const isLoomSupportEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_LOOM_SUPPORT);
  const isDesignIteration1Enabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_DESIGN_ITERATION_1);

  const selectedOffering = availableOfferings.find(
    (availableOffering) => availableOffering.id === formState.newPlan?.value
  );
  const isNewPlanPaid = selectedOffering?.pricingType === 'PAID';
  const isFreeToPaidTransition = isCurrentEntitlementFree && isNewPlanPaid;
  const offeringName = entitlement?.offering?.name?.toLowerCase();
  const selectedOfferingName = selectedOffering?.name?.toLowerCase();
  const selectedInvoiceGroup = defaultTo(availableInvoiceGroups, []).find(
    (ig) => ig?.id === formState?.invoiceGroupId?.value
  );
  // only check this on mount of the component
  const isInvoiceGroupFieldReadOnly = useMemo(() => !!(formState?.invoiceGroupId?.value ?? false), []);
  const shipToPartyId = defaultTo(selectedInvoiceGroup?.shipToParty?.id, undefined);
  // Using the default state of the entitlement offering id when no changes made in form
  const newOfferingIdWithFallback = formState?.newPlan?.value || entitlement.offeringId;
  const trialDays =
    availableOfferings.find((availableOffering) => availableOffering.id === newOfferingIdWithFallback)?.trial
      ?.lengthDays ?? 0;

  const pricingEligibility = useMemo(() => {
    return getPricingEligibility({
      invoiceGroup: selectedInvoiceGroup,
      billToPartyPricingEligibilty,
    });
  }, [selectedInvoiceGroup, billToPartyPricingEligibilty]);

  const filterOptions = [
    {
      key: 'currency',
      value: defaultTo(selectedInvoiceGroup?.currency, Currency.USD),
    },
    {
      key: 'offeringId',
      value: newOfferingIdWithFallback,
    },
    {
      key: 'billingCycle',
      value: entitlement?.pricingPlan?.billingFrequency || BillingFrequency.MONTHLY,
    },
  ];

  if (pricingEligibility) {
    filterOptions.push({
      key: 'planType',
      value: pricingEligibility,
    });
  }

  const { data: availablePricingPlansData, loading: isFetchingPricingPlans } = useQuery<
    FetchAvailablePricingPlansPpcQuery,
    FetchAvailablePricingPlansPpcQueryVariables
  >(fetchAvailablePricingPlans, {
    variables: {
      id: entitlement.id,
      transactionAccountId: txaId,
      filterOptions,
    },
  });

  const availablePricingPlans = availablePricingPlansData?.entitlementDetails?.availablePricingPlans;
  const defaultPricingPlan: any = getDefaultPricingPlan(availablePricingPlans as any);
  // Sending `null` for `pricingPlanId` for cases like Free entitlement, etc. otherwise error is thrown
  const pricingPlanId = defaultPricingPlan?.id || (availablePricingPlans?.length ? entitlement?.pricingPlan?.id : null);

  // This is added for case of Free to Standard/Premium Upgrade case
  // By adding this, we are updating currFormState in change-subscription-pane which is getting used at the time of placing order.
  React.useEffect(() => {
    if (!isFetchingPricingPlans && isCurrentEntitlementFree) {
      updateFormState({
        pricePlan: {
          pricingPlanType: defaultPricingPlan?.type,
          value: defaultPricingPlan?.id,
          chargeType: defaultPricingPlan?.chargeType,
          label: defaultPricingPlan?.planType,
          chargeElement: defaultPricingPlan?.chargeElement,
        },
      });
    }
  }, [isFetchingPricingPlans]);

  React.useEffect(() => {
    const billEstimate = async () => {
      try {
        const promotionIds = [];

        for (const key in formState) {
          // Note: Applied promo code is stored as promoCode-0
          if (regexForPromoCodeKey.test(key)) {
            // @ts-ignore
            const promoValue = formState[key];

            promoValue &&
              promotionIds.push({
                promotionDefinition: {
                  promotionCode: promoValue,
                },
              });
          }
        }

        // Note: Similar logic for sending only valid promo code is present for order request as well
        promotionIds.length = validPromoCodeData?.length; // To only send valid promo code for bill estimation

        let alreadyAttachedPromotions = entitlement?.promotions as PromotionInstanceDetail[];
        const availablePartnerPromotionId = availablePartnerPromotion && availablePartnerPromotion.id;

        if (availablePartnerPromotionId) {
          alreadyAttachedPromotions = alreadyAttachedPromotions?.filter(
            (promotion) => !(promotion?.type === PARTNER_DISCOUNT_TYPE)
          );
        }

        if (!isBillingCycleChanged) {
          defaultTo(
            differenceBy(
              alreadyAttachedPromotions as PromotionInstanceDetail[],
              defaultTo(
                formState?.promotionsToRemove?.map((item) => item?.promotion),
                []
              ),
              'id'
            ),
            []
          ).forEach((promotion: PromotionInstanceDetail) => {
            if (!isNil(promotion)) {
              promotionIds.push({ promotionInstanceId: promotion?.promotionInstanceId });
            }
          });
          if (!isOfferingChanged) {
            differenceBy(
              entitlement?.scheduledChanges?.promotions as PromotionInstanceDetail[],
              defaultTo(alreadyAttachedPromotions as PromotionInstanceDetail[], []),
              'promotionInstanceId'
            ).forEach((promotion: PromotionInstanceDetail) => {
              if (!isNil(promotion)) {
                const removedScheduledPromotionId = formState?.schedulePromotionsToRemove?.find(
                  (scheduledPromotion) => scheduledPromotion?.promotion?.id === promotion?.id
                );
                if (!removedScheduledPromotionId) {
                  if (promotion.isPromotionCustomisable) {
                    promotionIds.push(activePromotionsValueResolver(promotion));
                  } else {
                    promotionIds.push({ promotionDefinition: { promotionId: promotion?.id } });
                  }
                }
              }
            });
          }
        }

        if (availablePartnerPromotionId) {
          promotionIds.push({
            promotionDefinition: {
              promotionId: availablePartnerPromotionId as string,
            },
          });
        }

        onDiscretionaryEstimateChange({ loading: true });

        let orderIntentPayload: any = {
          transactionAccountId: defaultTo(entitlement?.transactionAccountId, ''),
          invoiceGroupId: defaultTo(formState?.invoiceGroupId?.value, ''),
          billingFrequency: (formState?.billingFrequency as unknown) as PricingPlanCycle,
        };
        setPricingPlanId?.(pricingPlanId);
        if (isEligibleForPremiumTrial || isEligibleForAdvancedTrial) {
          const requestBillingFrequency = formState?.billingFrequency;
          const requestOfferingId = entitlement?.offering?.id;

          orderIntentPayload = {
            ...orderIntentPayload,
            entitlements: [
              {
                entitlementId: entitlement.id,
                productId: entitlement.productId,
                pricingPlanId: pricingPlanId,
                offeringId: formState?.newPlan?.value ?? entitlement?.offeringId ?? undefined,
                pricingPlanType: formState?.pricing as PricingPlanType,
                orderType: OrderItemType.AMENDMENT_ORDER,
                skipTrial: formState?.skipTrial,
                behaviourAtEndOfTrial: BehaviourAtEndOfTrial.REVERT_TRIAL,
                addPromotions: [],
                chargeElement: get(
                  defaultEntitlementData,
                  `response.${requestBillingFrequency}.${requestOfferingId}.chargeQuantities[0].chargeElement`,
                  null
                ),
                chargeQuantity: get(
                  defaultEntitlementData,
                  `response.${requestBillingFrequency}.${requestOfferingId}.chargeQuantities[0].quantity`,
                  null
                ),
                chargeType: 'LICENSED',
                isExpiredPricingPlan: false,
                isImmediateAmendmentToFree: false,
                operations: [],
                parentEntitlementId: null,
                promotionsToRemove: [],
                removePromotions: [],
                nextBillingAnchorTimestamp: formState?.nextBillingAnchorTimestamp,
              },
            ],
          };
        } else {
          orderIntentPayload = {
            ...orderIntentPayload,
            entitlementId: entitlement.id,
            orderType: OrderItemType.AMENDMENT_ORDER,
            offeringId: formState?.newPlan?.value ?? entitlement?.offeringId ?? undefined,
            units: formState?.units ?? formState?.userTier?.value,
            pricingPlanType: formState?.pricing as PricingPlanType,
            planType:
              formState?.pricePlan?.pricingPlanType ?? defaultTo(get(entitlement, 'pricingPlan.pricingPlanType'), null),
            promotionIds,
            skipTrial: true,
            isBillingCycleChanged,
            pricingPlanId: pricingPlanId,
          };
        }

        const response =
          isEligibleForPremiumTrial || isEligibleForAdvancedTrial
            ? await getBillEstimateV2(orderIntentPayload)
            : await getBillEstimate(orderIntentPayload as OrderIntentRequest);

        setEstimatedBill({ response, loading: false });
        onDiscretionaryEstimateChange({
          discretionaryDiscountAmount: defaultTo(response?.discretionaryDiscountAmount, 0),
          loading: false,
        });

        const billingPeriod = response?.estimate?.items?.[0]?.billPeriod;
        setNewBillingStartDate(billingPeriod?.start ? formatTimeStampToDate(billingPeriod?.start, DATE_FORMAT) : '');
      } catch (error) {
        onDiscretionaryEstimateChange({ error, loading: false });
        setEstimatedBill({ error, loading: false });
      }
    };

    isFetchingPricingPlans || billEstimate();
  }, [formState?.invoiceGroupId?.value, isFetchingPricingPlans]);

  const renderPriceDifference = () => {
    return (
      <PriceDifference
        currentEstimatedBill={entitlement?.nextBillEstimate?.total || 0}
        estimatedBill={estimatedBill}
        setGrandTotal={setGrandTotal}
        shouldShowProratedCharge={
          isLoomSupportEnabled &&
          isLoomProduct(entitlement?.productId) &&
          entitlement?.billingFrequency === BillingFrequency.MONTHLY
        }
        currency={selectedInvoiceGroup?.currency ?? ''}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
      />
    );
  };

  const renderSandboxMessage = () => {
    return (
      <>
        Any sandboxes will be removed and data deleted.&nbsp;
        <a href={DELETE_SANDBOX_URL} target="_blank" rel="noreferrer">
          How to manage sandboxes.
        </a>
      </>
    );
  };

  const renderPaidToFreeSectionMessage = () => {
    if (isDesignIteration1Enabled) {
      return (
        <SectionMessage appearance="information">
          <ListContainer>
            <li>Paid feature access will be lost.</li>
            <li>Any remaining days in the billing cycle will be non-refundable.</li>
            <li>{renderSandboxMessage()}</li>
            <li>{addonCancellationWarningMessage}</li>
          </ListContainer>
        </SectionMessage>
      );
    }

    return (
      <SectionMessage appearance="information">
        <ul>
          <li>The Free plan will start immediately.</li>
          <li>Access to the paid plan features will be lost.</li>
          <li>Any remaining days in the billing cycle will be non-refundable.</li>
          <li>
            If applicable, any sandbox instances will be removed and data deleted -&nbsp;
            <a href={DELETE_SANDBOX_URL} target="_blank" rel="noreferrer">
              learn more
            </a>
            .
          </li>
        </ul>
      </SectionMessage>
    );
  };

  const renderDowngradeSectionMessage = (isApp: boolean) => {
    const renewalText = newBillingStartDate ? ` on ${newBillingStartDate}` : '';

    if (isDesignIteration1Enabled) {
      const renderDowngradeMessage = () => {
        return <>Downgrade will be scheduled for {newBillingStartDate}, at the end of the current billing cycle.</>;
      };

      return (
        <SectionMessage appearance="information">
          {isApp ? (
            <p>Advanced feature access will be lost.</p>
          ) : (
            <ListContainer>
              {newBillingStartDate && <li>{renderDowngradeMessage()}</li>}
              <li>Premium feature access will be lost.</li>
              <li>{renderSandboxMessage()}</li>
              <li>{addonCancellationWarningMessage}</li>
            </ListContainer>
          )}
        </SectionMessage>
      );
    }

    return (
      <SectionMessage appearance="information">
        <ul>
          {newBillingStartDate && (
            <li>
              The {startCase(selectedOfferingName)} plan will start on {newBillingStartDate}.
            </li>
          )}
          <li>Access to some {startCase(offeringName)} plan features will be lost.</li>
          <li>
            From now until your renewal{renewalText}, the subscription will remain on {startCase(offeringName)}.
          </li>
          <li>
            If applicable, any sandbox instances will be removed and data deleted -&nbsp;
            <a href={DELETE_SANDBOX_URL} target="_blank" rel="noreferrer">
              learn more
            </a>
            .
          </li>
        </ul>
      </SectionMessage>
    );
  };

  const trialEndDate = formatDateToMonthDay(addDaysToDate(new Date(), trialDays).getTime());
  const trialEndsText = trialDays ? (
    <>
      {' '}
      on <strong>{trialEndDate}</strong>
    </>
  ) : (
    ''
  );
  const renderFreeToPaidTrialSectionMessage = () => {
    // Applies for Free as well as Trial entitlements
    return (
      <SectionMessage appearance="information">
        {!isDesignIteration1Enabled && <p>This monthly plan starts with a no-cost {trialDays} days trial.</p>}
        <p className="mt-8">
          The new plan will start immediately, and we won't charge you until the trial ends{trialEndsText}
          <strong> - you'll then pay the price estimate listed below.</strong> Actual amount charged may be different if
          the number of users has changed by the end of the trial period.
        </p>
        <p className="mt-8">
          You can go back to the Free plan anytime by selecting ‘Change subscription’ from this subscription screen.
        </p>
      </SectionMessage>
    );
  };

  const renderPaidToPaidTrialSectionMessage = () => {
    return (
      <SectionMessage appearance="information">
        {!isDesignIteration1Enabled && <p>This monthly plan will start with a no-cost, {trialDays} days trial.</p>}
        <p className="mt-8">
          The new plan (and access to its features) will start immediately, and we won't charge you for it until the
          trial ends{trialEndsText}
          <strong> - you'll then pay the price estimate listed below.</strong>
        </p>
        <p className="mt-8">
          Actual amount charged may be different if the number of users has changed by the end of the trial period.
        </p>
        <p className="mt-8">You can end the trial anytime by selecting ‘End trial’ from this subscription screen.</p>
      </SectionMessage>
    );
  };

  const renderAppropriateSectionMessages = () => {
    if (isPaidToFreeTransition) {
      return renderPaidToFreeSectionMessage();
    } else if (isPremiumToStandardTransition || isAppEditionDowngrade) {
      return renderDowngradeSectionMessage(!!entitlement?.isApp);
    } else {
      return isSkipTrialChecked || !!formState?.skipTrial ? (
        <SectionMessage appearance="information">
          {`This new monthly ${
            formState?.newPlan?.label ? formState?.newPlan?.label + ' ' : ''
          }plan will start immediately. `}
          <strong>You'll pay the price estimate listed below.</strong>
        </SectionMessage>
      ) : isFreeToPaidTransition ? (
        renderFreeToPaidTrialSectionMessage()
      ) : (
        renderPaidToPaidTrialSectionMessage()
      );
    }
  };

  const getNextBillDate = () => {
    if (!isSkipTrialChecked && trialDays) {
      return formatDateToMonthDay(addDaysToDate(new Date(), trialDays + 1).getTime());
    }

    return newBillingStartDate;
  };

  const getInfoMessage = () => {
    if (isDesignIteration1Enabled) {
      const nextBillDate = getNextBillDate();

      if (formState.newPlan?.label?.toUpperCase() === PricingType.FREE) {
        return 'Plan starts immediately';
      }

      if (isSkipTrialChecked) {
        return nextBillDate ? `New plan starts on ${nextBillDate}` : undefined;
      }

      if (trialDays) {
        return `${trialDays}-days free trial`;
      }
    }
  };

  const getHeading = () => {
    if (isDesignIteration1Enabled && formState?.newPlan?.label) {
      return `New ${formState?.newPlan?.label} plan`;
    }
    return 'New plan';
  };

  const renderCurrentPlanSummary = () => {
    if (!isDesignIteration1Enabled) {
      return (
        <CurrentPlanSummary
          heading="Current plan"
          entitlement={entitlement}
          isBillingCycleChanged={isBillingCycleChanged}
          isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
        />
      );
    }
  };

  const renderReviewChangesTitle = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Text size="medium" weight="bold">
          Review changes
        </Text>
      );
    }
    return <h2>Review changes</h2>;
  };

  const renderBillingProfileSection = () => {
    return (
      <InvoiceGroupFormField
        isRequired={!isDesignIteration1Enabled}
        isReadOnly={isInvoiceGroupFieldReadOnly}
        availableInvoiceGroups={availableInvoiceGroups as InvoiceGroup[]}
        entitlementBillingInvoiceGroupId={formState?.invoiceGroupId?.value}
        fieldLabel="Billing profile"
        maxWidth={null}
        onChange={(value) => {
          setEstimatedBill({ loading: true });
          updateFormState({
            invoiceGroupId: value as InvoiceGroupSelectItem,
          });
        }}
        currentPlan={entitlement?.offering?.pricingType as PricingType}
        isSearchable={isSearchable}
      />
    );
  };

  const renderUpdatePONumberButton = () => {
    const handleClick = () => {
      setCanUpdatePO(true);
    };
    if (isDesignIteration1Enabled) {
      return (
        <Button testId="add-po-button" onClick={handleClick} appearance="subtle">
          <Text color="color.link" weight="medium">
            Update PO number
          </Text>
        </Button>
      );
    }

    return (
      <Button
        testId="add-po-button"
        onClick={() => setCanUpdatePO(true)}
        iconBefore={<AddIcon label="add" />}
        appearance="link"
      >
        Update PO number
      </Button>
    );
  };

  const renderUpdatePONumber = () => {
    return canUpdatePO ? (
      <POUpdate
        setCanUpdatePO={setCanUpdatePO}
        billingGroupId={formState?.invoiceGroupId?.value}
        entitlement={entitlement}
        shipToPartyId={shipToPartyId}
      />
    ) : (
      renderUpdatePONumberButton()
    );
  };

  const renderNewPlanSummary = () => {
    return (
      <NewPlanSummary
        heading={getHeading()}
        entitlement={entitlement}
        formState={formState}
        isExistingAnnualPlanForJCA={isExistingAnnualPlanForJCA}
        setCurrentProgressIndex={setCurrentProgressIndex}
        isSkipTrialChecked={isSkipTrialChecked}
        defaultPricingPlan={defaultPricingPlan}
        isFetchingPricingPlans={isFetchingPricingPlans}
        isProductIconRequired={isDesignIteration1Enabled}
        infoMessage={getInfoMessage()}
        isDesignIteration1Enabled={isDesignIteration1Enabled}
        nextBillDate={getNextBillDate()}
      />
    );
  };

  const renderNoteField = () => {
    return (
      <Field label={'Note'} name="notes">
        {({ fieldProps }) => (
          <Textfield
            className="internal-notes"
            {...fieldProps}
            name={'notes'}
            testId="notes-field"
            placeholder="Enter text"
          />
        )}
      </Field>
    );
  };

  if (isDesignIteration1Enabled) {
    return (
      <div className="review-change-section">
        {renderReviewChangesTitle()}
        {renderCurrentPlanSummary()}
        {renderNewPlanSummary()}
        {renderBillingProfileSection()}
        <div className="mt-8">{renderUpdatePONumber()}</div>
        {renderNoteField()}
        <div className="mt-16 mb-16">{renderAppropriateSectionMessages()}</div>
        {renderPriceDifference()}
      </div>
    );
  }

  return (
    <div className="review-change-section">
      {renderReviewChangesTitle()}
      {renderCurrentPlanSummary()}
      {renderNewPlanSummary()}
      <div className="mt-4 mb-4">{renderAppropriateSectionMessages()}</div>
      {renderPriceDifference()}
      <h2>Confirm changes</h2>
      {renderBillingProfileSection()}
      {renderUpdatePONumber()}
      {renderNoteField()}
    </div>
  );
};
